import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import type { LinearRGB } from '../../../core/color';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { gableRoof } from '../roofSolids';
import { horizontalCap, octahedronSolid, orientedFace, planPoint, pyramidOverRing, ringPoints } from '../solids';
import { pointedWindow } from '../panels';
import { centredWindow, gableWall, openedWall, edgePanel, rectWall, type MasonryStyles } from './masonry';
import { planBounds, planFrameOf, meshFrameOf, toLayout } from './planFrame';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

export interface DoelenColors extends PaintedChapelColors { readonly tile: LinearRGB; }
interface Body { readonly u0: number; readonly u1: number; readonly v0: number; readonly v1: number;
  readonly eave: number; readonly ridge: number; }
const ringOf = (b: Body): Ring => [[b.u0, b.v0], [b.u1, b.v0], [b.u1, b.v1], [b.u0, b.v1]];

/** The two late-medieval halls are separate objects at the wall-side ends of their
 * shooting grounds. The 1536 Anthonisz drawing, 1538 painting and 1544 woodcut establish
 * the unequal hall/annex hierarchy; dimensions, window spacing and hidden faces are estimates.
 * No 1606 Bushuis, 17th-century sculpted gateway or 1733 list facade is backdated. */
export function doelenHallPlan(landmark: Landmark) {
  const large = landmark.id === 'landmark-handboogdoelen';
  const p = planFrameOf(landmark.footprint, landmark.heading), b = planBounds(p);
  const uc = (b.u0 + b.u1) / 2, vc = (b.v0 + b.v1) / 2;
  const scale = Math.min((b.u1 - b.u0) / (large ? 28 : 24),
    (b.v1 - b.v0) / (large ? 20 : 18));
  const body = (u0: number, u1: number, v0: number, v1: number, eave: number, ridge: number): Body =>
    ({ u0: uc + u0 * scale, u1: uc + u1 * scale, v0: vc + v0 * scale,
      v1: vc + v1 * scale, eave, ridge });
  const main = large ? body(-6.3, 6.3, -7, 8, 11.2, 16.1) : body(-6, 6, -6, 7.3, 8.5, 12.6);
  const annex = large ? body(6.3, 12.5, -4, 6, 7.2, 10.1) : body(-10, -6, -3, 5, 6.2, 8.5);
  const stair = large ? { centre: [uc - 3 * scale, vc - 8.15 * scale] as Vec2,
    radius: 1.35 * scale, eave: 14.2, top: 17.4 } : null;
  return { plan: p, frame: meshFrameOf(p), main, annex, stair, large };
}

function windows(width: number, height: number, grand: boolean): Ring[] {
  const count = Math.max(1, Math.round(width / (grand ? 4.4 : 4.6)));
  const w = grand ? 1.35 : 0.9;
  const holes = Array.from({ length: count }, (_, i) => {
    const x = width * (i + 0.5) / count;
    return [[x - w / 2, 1.7], [x + w / 2, 1.7], [x + w / 2, 3.2], [x - w / 2, 3.2]] as Ring;
  });
  const sill = height > 10 ? 6.2 : height > 7.5 ? 4.5 : 3.6;
  const spring = height - (grand ? 2.4 : 2.2);
  return [...holes, ...Array.from({ length: count }, (_, i) =>
    pointedWindow(width * (i + 0.5) / count, sill, w, spring, 3))];
}

function steppedOutline(width: number, eave: number, ridge: number): Ring {
  // Six short ledges on each half of the late-Gothic front, with a raised centre.
  const steps = 6, half = width / 2, run = (half - 0.65) / steps, rise = (ridge - eave) / steps;
  const right: Vec2[] = [[width, eave + rise + 0.35]];
  for (let i = 1; i <= steps; i++) {
    right.push([width - i * run, eave + i * rise + 0.35]);
    if (i < steps) right.push([width - i * run, eave + (i + 1) * rise + 0.35]);
  }
  const left = right.slice().reverse().map(([x, y]): Vec2 => [width - x, y]);
  return [[0, 0], [width, 0], ...right, ...left];
}

function hallBody(builder: MeshBuilder, styles: MasonryStyles, frame: Frame,
  b: Body, grand: boolean): void {
  const ring = ringOf(b);
  ring.forEach((a, i) => {
    const end = ring[(i + 1) % 4]!, width = Math.hypot(end[0] - a[0], end[1] - a[1]);
    const holes = windows(width, b.eave, grand);
    if (i === 0 || i === 2) {
      if (i === 2) {
        // A real recessed doorway on the wall-facing front; the upper window row stays.
        const centre = width / 2, half = 0.68;
        const clear = holes.filter((h) => Math.max(...h.map((p) => p[1])) > 4 ||
          Math.max(...h.map((p) => p[0])) < centre - 1 || Math.min(...h.map((p) => p[0])) > centre + 1);
        clear.push([[centre - half, 0], [centre + half, 0], [centre + half, 2.6], [centre - half, 2.6]]);
        holes.splice(0, holes.length, ...clear);
      }
      holes.push(...centredWindow(width, b.eave + 0.45, b.ridge - 0.65, 0.12));
      if (grand && i === 2) {
        const outline = steppedOutline(width, b.eave, b.ridge);
        openedWall(builder, styles, frame, edgePanel(a, end), outline, holes);
        // The ledges have actual thickness, rather than a decal on a plain roof triangle.
        // Only the masonry above the ordinary roof slope has a exposed rear face;
        // duplicating the whole gable behind it would erase the solid's closure.
        const upper: Ring = [[width, b.eave], ...outline.slice(2), [0, b.eave], [width / 2, b.ridge]];
        // Both reveals recess 25 cm. The parapet must be thicker than both reveals
        // together so its back glazing cannot project through the front surface.
        const inset = 0.6;
        openedWall(builder, styles, frame,
          edgePanel([end[0], end[1] - inset], [a[0], a[1] - inset]), upper, []);
        const profile: Ring = [[width, b.eave], ...outline.slice(2), [0, b.eave]];
        for (let k = 0; k + 1 < profile.length; k++) {
          const one = profile[k]!, two = profile[k + 1]!;
          const at = (p: Vec2, inset: number) => planPoint(a[0] - p[0], p[1], a[1] - inset);
          const horizontal = Math.abs(one[1] - two[1]) < 1e-6;
          orientedFace(builder, styles.wall, frame,
            [at(one, 0), at(two, 0), at(two, inset), at(one, inset)],
            horizontal ? [0, 1, 0] : [one[0] > width / 2 ? -1 : 1, 0, 0]);
        }
        const lower: Ring = [[width, b.eave], [width / 2, b.ridge], [0, b.eave]];
        for (let k = 0; k < 2; k++) {
          const one = lower[k]!, two = lower[k + 1]!;
          const at = (q: Vec2, depth: number) => planPoint(a[0] - q[0], q[1], a[1] - depth);
          orientedFace(builder, styles.wall, frame,
            [at(one, 0), at(two, 0), at(two, inset), at(one, inset)], [0, -1, 0]);
        }
      } else gableWall(builder, styles, frame, [a, end], { eaveY: b.eave, apexY: b.ridge }, holes);
    } else rectWall(builder, styles, frame, [a, end], [0, b.eave], holes);
  });
  const turned = { u0: b.v0, u1: b.v1, v0: -b.u1, v1: -b.u0 };
  gableRoof(builder, styles, { origin: frame.origin, yaw: frame.yaw + Math.PI / 2 },
    { ...turned, eaveY: b.eave, ridgeY: b.ridge, ends: { start: false, end: false } });
}

export function buildDoelenHall(builder: MeshBuilder, landmark: Landmark,
  colors: DoelenColors, ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY): number {
  const before = builder.triangleCount, p = doelenHallPlan(landmark);
  const styles = paintedChapelStyles(colors, ids, tracery);
  const tile = { ...styles.roof, color: colors.tile, surface: SURFACE.roofTile };
  hallBody(builder, { ...styles, roof: tile }, p.frame, p.main, p.large);
  hallBody(builder, styles, p.frame, p.annex, false);
  if (p.stair) {
    const tower = p.stair, ring = ringPoints(tower.centre, tower.radius, 8, Math.PI / 8);
    ring.forEach((a, i) => {
      const b = ring[(i + 1) % ring.length]!, width = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const holes = [2.2, 5.9, 9.8].flatMap((y) => centredWindow(width, y, y + 2.15, 0.38));
      rectWall(builder, styles, p.frame, [a, b], [0, tower.eave], holes);
    });
    horizontalCap(builder, styles.wall, p.frame, ring, 0, false);
    pyramidOverRing(builder, styles, p.frame, ring, tower.eave, { plan: tower.centre, y: tower.top });
    octahedronSolid(builder, styles.wall, p.frame,
      planPoint(tower.centre[0], tower.top + 0.1, tower.centre[1]), 0.15);
  }
  return builder.triangleCount - before;
}

export function doelenObstacles(landmark: Landmark): readonly Ring[] {
  const p = doelenHallPlan(landmark), rings = [ringOf(p.main), ringOf(p.annex)];
  if (p.stair) rings.push(ringPoints(p.stair.centre, p.stair.radius, 8, Math.PI / 8));
  return rings.map((ring) => ring.map((q) => toLayout(p.plan, q)));
}
